import { Localized, useLocalization } from "@fluent/react"
import AddCircleIcon from "@mui/icons-material/AddCircle"
import EditIcon from "@mui/icons-material/Edit"
import { Alert, Autocomplete, Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material"
import { useState } from "react"
import { CancelButton } from "../../../../../app/components/CancelButton"
import { RequiredError } from "../../../../../app/components/RequiredError"
import { SaveButton } from "../../../../../app/components/SaveButton"
import { AppId } from "../../../../../app/appTypes"
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks"
import { showError, showSuccess } from "../../../../notifications/notificationsSlice"
import { isDocEditable } from "../../../documentSlice"
import {
    createDocumentReference,
    DocumentReference,
    DocumentReferenceSaveDto,
    updateDocumentReference,
} from "../docReferencesSectionApi"

export interface AddEditDocReferenceDialogProps {
    documentId: AppId
    sectionId: string
    documentReference: DocumentReference | null
    mode: "create" | "edit"
    onSuccessfulSave: (documentReference: DocumentReference) => void
    onCancelClick: () => void
}

export const AddEditDocReferenceDialog = ({ documentId, mode, documentReference, onCancelClick, onSuccessfulSave }: AddEditDocReferenceDialogProps) => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()

    const [saving, setSaving] = useState(false)
    const [serverError, setServerError] = useState("")
    const [referencedDocumentId, setReferencedDocumentId] = useState<string>(documentReference?.referencedDocumentId?.toString() ?? "")
    const [referenceType, setReferenceType] = useState(documentReference?.referenceType ?? "")
    const [referenceNumber, setReferenceNumber] = useState(documentReference?.referenceNumber ?? "")
    const [titleValue, setTitleValue] = useState(documentReference?.title ?? "")
    const [url, setUrl] = useState(documentReference?.url ?? "")
    const [description, setDescription] = useState(documentReference?.description ?? "")
    const [validationErrors, setValidationErrors] = useState(false)
    const isEditable = useAppSelector(isDocEditable)

    const referenceTypeError = validationErrors && referenceType.trim() === ""

    const handleOkClick = async () => {
        setValidationErrors(true)
        if (!documentId || referenceType.trim() === "") {
            return
        }

        if (mode === "edit" && !documentReference?.id) {
            return
        }

        setSaving(true)
        setServerError("")
        try {
            const payload: DocumentReferenceSaveDto = {
                referencedDocumentId: referencedDocumentId.trim() === "" ? null : Number(referencedDocumentId),
                referenceType: referenceType.trim() || null,
                referenceNumber: referenceNumber.trim() || null,
                title: titleValue.trim() || null,
                url: url.trim() || null,
                description: description.trim() || null,
            }

            if (payload.referencedDocumentId !== null && Number.isNaN(payload.referencedDocumentId)) {
                setServerError(l10n.getString("error", null, "Error"))
                setSaving(false)
                return
            }

            let result

            if (mode === "create") {
                result = await dispatch(createDocumentReference({ documentId, dto: payload }))

                if (createDocumentReference.fulfilled.match(result)) {
                    const createdReference = result.payload as DocumentReference
                    dispatch(showSuccess("saved"))
                    onSuccessfulSave(createdReference)
                    return
                }
            }

            if (mode === "edit") {
                result = await dispatch(updateDocumentReference({ documentId, id: String(documentReference!.id), dto: payload }))

                if (updateDocumentReference.fulfilled.match(result)) {
                    const updatedReference = result.payload as DocumentReference
                    dispatch(showSuccess("saved"))
                    onSuccessfulSave(updatedReference)
                    return
                }
            }

            let errorMsg = "error"
            if (result?.payload?.kind === "http" && result.payload.problem) {
                errorMsg = result.payload.problem.title
            }
            setServerError(errorMsg)

        } catch {
            dispatch(showError("error"))
        } finally {
            setSaving(false)
        }
    }
    const referenceTypeOptions = [
        "CAPA",
        "CHC",
        "URS",
        "SOP",
        "FDS",
        "FS",
        "DS",
        "RA",
        "DQ",
        "IQ",
        "OQ",
        "PQ"
    ];
    const content = <>
        <Autocomplete
            freeSolo
            options={referenceTypeOptions}
            value={referenceType || ""}
            inputValue={referenceType || ""}
            disabled={!isEditable}
            onInputChange={(_, newInputValue) => {
                setReferenceType(newInputValue.toUpperCase());
            }}
            onChange={(_, newValue) => {
                setReferenceType((newValue ?? "").toUpperCase());
            }}
            ListboxProps={{
                sx: {
                    maxHeight: 220,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                    p: 1
                }
            }}
            renderOption={(props, option) => (
                <li
                    {...props}
                    key={option}
                    style={{
                        width: "auto",
                        padding: 0
                    }}
                >
                    <Chip
                        label={option}
                        size="small"
                        variant="outlined"
                        sx={{
                            cursor: "pointer",
                            fontWeight: 500
                        }}
                    />
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    error={referenceTypeError}
                    helperText={referenceTypeError && <RequiredError />}
                    required
                    margin="normal"
                    variant="outlined"
                    label={l10n.getString("document-type", null, "_Reference type")}
                    fullWidth
                    inputProps={{
                        ...params.inputProps,
                        style: { textTransform: "uppercase" }
                    }}
                />
            )}
        />
        <TextField
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
            disabled={!isEditable}
            margin="normal"
            variant="outlined"
            label={l10n.getString("number", null, "_Reference number")}
            fullWidth
        />

        <TextField
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            disabled={!isEditable}
            margin="normal"
            variant="outlined"
            label={l10n.getString("title", null, "Title")}
            fullWidth
        />

        <TextField
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={!isEditable}
            margin="normal"
            variant="outlined"
            label="Link"
            fullWidth
        />

        <TextField
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={!isEditable}
            multiline
            minRows={3}
            margin="normal"
            variant="outlined"
            label={l10n.getString("description", null, "Description")}
            fullWidth
        />

        {serverError && (
            <Alert severity="error" sx={{ mt: 2 }}>
                {serverError}
            </Alert>
        )}
    </>

    let dialogTitle = <></>
    switch (mode) {
        case 'create':
            dialogTitle = <>
                <AddCircleIcon fontSize="small" sx={{ mr: 1 }} color="success" />
                <Localized id="add">
                    <span>Add</span>
                </Localized>

            </>
            break;
        case 'edit':
            dialogTitle = <>
                <EditIcon fontSize="small" sx={{ mr: 1 }} color="info" />
                <Localized id="edit">
                    <span>Edit</span>
                </Localized>
            </>
            break;
    }


    return <>
        <Dialog key="addEditDocumentReference" open={true} maxWidth="md" fullWidth keepMounted={false}>
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "#f9f9f9",
                    color: "text.primary",
                    fontWeight: "bold",
                }}
            >
                {dialogTitle}
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    [`& > div`]: {
                        flexGrow: 1,
                    },
                }}>{content}</Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
                <SaveButton disabled={!isEditable} loading={saving} onClick={handleOkClick}></SaveButton>
                <CancelButton loading={saving} onClick={onCancelClick}></CancelButton>
            </DialogActions>
        </Dialog>
    </>
}
